import type { ReactNode } from 'react';
import Image from '@/lib/media/image';
import Link from '@/lib/nav/link';
import { BackIcon as ArrowLeft } from '@/components/icons/line/back';
import { Logo } from '@/components/branding/Logo';
import { ONBOARDING_PHOTOS } from '@/lib/marketing/photo-assets';
import { shouldShowAuthBackLink } from '@/lib/pwa/app-launch';

export const AUTH_PAGE_HEADING = 'Welcome';
export const AUTH_PAGE_SUBTEXT =
  'Plan trips together. Sign in or create your account to get started.';

type AuthPageShellProps = {
  children: ReactNode;
};

export function AuthPageShell({ children }: AuthPageShellProps) {
  const authPhoto = ONBOARDING_PHOTOS.auth;
  const showBackLink = shouldShowAuthBackLink();

  return (
    <main className="relative grid min-h-dvh bg-[#F5F0ED] text-[#1F1C1C] lg:grid-cols-[1fr_0.92fr]">
      {showBackLink ? (
        <Link
          href="/"
          data-auth-back-link
          className="absolute left-4 top-4 z-20 inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/85 px-4 py-2 text-sm font-bold text-black/65 shadow-sm backdrop-blur transition hover:bg-white hover:text-black md:left-8 md:top-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Link>
      ) : null}

      <section className="relative hidden min-h-dvh overflow-hidden lg:block">
        <Image
          src={authPhoto.url}
          alt={authPhoto.alt}
          fill
          priority
          sizes="52vw"
          className="object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/35 via-transparent to-transparent" />
      </section>

      <section className="flex min-h-dvh items-center justify-center px-4 py-12">
        <div className="mx-auto w-full max-w-[460px]">
          <div className="mb-7 flex flex-col items-center text-center">
            <Logo variant="full" width={220} height={80} className="h-16 w-auto" />
            <h1 className="mt-6 font-display text-3xl font-bold">{AUTH_PAGE_HEADING}</h1>
            <p className="mt-2 text-sm leading-6 text-black/55">{AUTH_PAGE_SUBTEXT}</p>
          </div>
          {children}
          <AuthLegalNotice />
        </div>
      </section>
    </main>
  );
}

function AuthLegalNotice() {
  return (
    <div className="mx-auto mt-5 max-w-[26rem] space-y-2 text-center text-xs font-medium leading-5 text-black/52">
      <p>
        By continuing, you agree to Roam Together&apos;s{' '}
        <Link
          href="/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="font-bold text-[#FA5E4F] underline-offset-2 hover:underline"
        >
          Terms of Service
        </Link>{' '}
        and{' '}
        <Link
          href="/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="font-bold text-[#FA5E4F] underline-offset-2 hover:underline"
        >
          Privacy Policy
        </Link>
        .
      </p>
      <p>
        If you add dietary, allergy, accessibility, or other health-related notes for your family,
        you consent to our storing and using them only to personalize your trips, as described in
        our{' '}
        <Link
          href="/privacy/health-notice"
          target="_blank"
          rel="noopener noreferrer"
          className="font-bold text-[#FA5E4F] underline-offset-2 hover:underline"
        >
          Consumer Health Data Notice
        </Link>
        .
      </p>
    </div>
  );
}
